import { MoreHorizontal, Mail, Trash2, Edit } from 'lucide-react'
import {
  Card,
  CardContent,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui'
import { getRoleDisplayName } from '@/hooks'
import { cn } from '@/lib/utils'
import type { FamilyMember, FamilyRole } from '@/types'

interface MemberCardProps {
  member: FamilyMember
  currentUserRole: FamilyRole | null
  onEdit?: (member: FamilyMember) => void
  onRemove?: (member: FamilyMember) => void
  isRemoving?: boolean
}

const ROLE_COLORS: Record<FamilyRole, { bg: string; text: string }> = {
  PRIMARY_CAREGIVER: { bg: 'bg-primary-100', text: 'text-primary-700' },
  CAREGIVER: { bg: 'bg-info-100', text: 'text-info-700' },
  PARENT: { bg: 'bg-success-100', text: 'text-success-700' },
  VIEWER: { bg: 'bg-gray-100', text: 'text-gray-700' },
}

export function MemberCard({
  member,
  currentUserRole,
  onEdit,
  onRemove,
  isRemoving = false,
}: MemberCardProps) {
  const canManage = currentUserRole === 'PRIMARY_CAREGIVER'
  const isPrimaryCaregiver = member.role === 'PRIMARY_CAREGIVER'
  const roleColors = ROLE_COLORS[member.role]

  const initials = member.user.full_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-gray-100 text-gray-600">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{member.user.full_name}</span>
                <Badge className={cn(roleColors.bg, roleColors.text, 'border-0')}>
                  {getRoleDisplayName(member.role)}
                </Badge>
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-sm text-gray-500">
                <Mail className="h-3 w-3" />
                {member.user.email}
              </div>
              {member.permissions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {member.permissions.map((permission) => (
                    <Badge key={permission} variant="outline" className="text-xs">
                      {formatPermission(permission)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {canManage && !isPrimaryCaregiver && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={isRemoving}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(member)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit permissions
                  </DropdownMenuItem>
                )}
                {onRemove && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onRemove(member)}
                      className="text-error-600 focus:text-error-600"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove from family
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function formatPermission(permission: string): string {
  return permission
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}
